import axios from 'axios';
import URL_CONST from '../../../config/url';
import {PagingParams} from '../../../types/dataService/PagingParams';
import useSWR from 'swr';
import {
  DatasetMetadata,
  DatasetMetadataBinaryItem,
  DatasetMetadataResponse,
} from '../../../types/dataService/DatasetMetadata';
import {useEffect, useState} from 'react';
import {formatBinariesToLocal} from '../../../store/dataService/datasetMetaDataUtil';
import {flatten, sumBy} from 'lodash';
import {ItemType} from './FileViewer';
import {getHeaders} from '../../../store/dataService';

export const DEFAULT_PAGE_PARAMS = {
  page: 0,
  size: 50,
  sort: ['created', 'asc'],
};

export const DEFAULT_PAGE_PARAMS_LABEL = {
  ...DEFAULT_PAGE_PARAMS,
  size: 20,
};

export const DEFAULT_PAGE_PARAMS_LIST = {
  ...DEFAULT_PAGE_PARAMS,
  size: 100,
};
export const DEFAULT_RSQL = ['type=in=(binary/image,binary/video)'];

export const DEFAULT_FIELDS = 'id,created,mediaType,originalPath,size,updated';
export const FIELDS_LIST = DEFAULT_FIELDS + ',width,height';

export type DatasetBinaries = {
  list: DatasetMetadataBinaryItem[];
  formattedBinaries: ItemType[];
  totalElements: number;
  totalPages?: number;
};

type DatasetLoaderProps = {
  datasetId?: string | null;
  version?: number;
  previewSize?: number;
  pageParams?: PagingParams;
  //Category Ids
  rsql?: string[];
  fields?: string;
};

const INITIAL_DATA = {
  list: [],
  formattedBinaries: [],
  totalElements: 0,
  totalPages: undefined,
};

export const useDatasetLoader = ({
  datasetId,
  version,

  pageParams = DEFAULT_PAGE_PARAMS,
  previewSize = 93,
  rsql = DEFAULT_RSQL,
  fields = DEFAULT_FIELDS,
}: DatasetLoaderProps) => {
  const [data, setData] = useState<DatasetBinaries>(INITIAL_DATA);

  const fetcher = async (query: string) =>
    axios
      .get<DatasetMetadataResponse>(URL_CONST.DSS_DATASET_METADATA, {
        headers: await getHeaders(),
        params: {
          datasetId: datasetId || '',
          version: version || 0,
          page: pageParams.page,
          size: pageParams.size,
          sort: pageParams.sort?.join(),
          rsql: query,
          fields,
        },
      })
      .then(res => res.data.body);

  const multiFetcher = () =>
    Promise.all(rsql.map(query => fetcher(query))).then(res => res);

  const {data: remoteData, error} = useSWR<any>(
    [datasetId, version, previewSize, pageParams, rsql, fields],
    multiFetcher
  );

  useEffect(() => {
    if (datasetId && remoteData) {
      const rData = remoteData as DatasetMetadata[];

      const list: DatasetMetadataBinaryItem[] = flatten(
        remoteData.map((data: DatasetMetadata) => data.list)
      );
      const total = sumBy(rData, i => i.totalElements);
      setData({
        list,
        formattedBinaries: formatBinariesToLocal(list, datasetId),
        totalElements: total,
        totalPages: rData.length === 1 ? rData[0].totalPages : undefined,
      });
    } else {
      setData(INITIAL_DATA);
    }
  }, [remoteData, datasetId]);

  return {
    ...data,
    isLoading: !error && !remoteData,
    isError: error,
  };
};
